import { useEffect, useState } from 'react';
import { resolveMatchMediaWindow } from './useMediaQuery';
import {
  VISIBILITY_BREAKPOINT_MEDIA,
  type VisibilityBreakpointToken,
} from '../utils/binding-enhancer/visibility';

const BREAKPOINT_TOKENS = Object.keys(
  VISIBILITY_BREAKPOINT_MEDIA,
) as VisibilityBreakpointToken[];

type ActiveListener = (active: ReadonlySet<string>) => void;

/**
 * Process-wide matchMedia subscription so N {@link useActiveVisibilityBreakpoints}
 * callers share three listeners instead of 3×N.
 */
let mediaLists: Array<{
  token: VisibilityBreakpointToken;
  media: MediaQueryList;
}> | null = null;
let mediaChangeHandler: (() => void) | null = null;
let currentActive: ReadonlySet<string> = new Set();
const subscribers = new Set<ActiveListener>();

function computeActive(
  lists: NonNullable<typeof mediaLists>,
): ReadonlySet<string> {
  const next = new Set<string>();
  for (const { token, media } of lists) {
    if (media.matches) {
      next.add(token);
    }
  }
  return next;
}

function notifySubscribers(active: ReadonlySet<string>): void {
  currentActive = active;
  // forEach (not for...of): ts-jest's for-of transform over Set is empty
  // in this package's Jest runs; forEach/Array.from stay correct.
  subscribers.forEach((listener) => {
    listener(active);
  });
}

function startSharedSubscription(): void {
  if (mediaLists !== null) {
    return;
  }
  if (typeof window === 'undefined') {
    return;
  }

  let targetWindow: Window;
  try {
    targetWindow = resolveMatchMediaWindow();
  } catch {
    return;
  }

  mediaLists = BREAKPOINT_TOKENS.map((token) => ({
    token,
    media: targetWindow.matchMedia(VISIBILITY_BREAKPOINT_MEDIA[token]),
  }));

  mediaChangeHandler = () => {
    if (mediaLists === null) return;
    notifySubscribers(computeActive(mediaLists));
  };

  for (const { media } of mediaLists) {
    media.addEventListener('change', mediaChangeHandler);
  }

  notifySubscribers(computeActive(mediaLists));
}

/**
 * Test-only: tear down the shared matchMedia subscription between suites.
 * @internal
 */
export function resetActiveVisibilityBreakpointsSubscriptionForTests(): void {
  if (mediaLists !== null && mediaChangeHandler !== null) {
    for (const { media } of mediaLists) {
      media.removeEventListener('change', mediaChangeHandler);
    }
  }
  mediaLists = null;
  mediaChangeHandler = null;
  currentActive = new Set();
  subscribers.clear();
}

/**
 * Active `_meta.visibility.showOn` breakpoint tokens for the current
 * viewport (iframe-aware via {@link resolveMatchMediaWindow}).
 *
 * SSR / hydration: starts as an empty set and only populates after mount
 * (`useEffect`). That keeps server HTML and the client's first paint in
 * agreement (no hydration warnings). `mode: 'mount'` may briefly hide
 * then remount; authors who need zero-flash should use `mode: 'css'`.
 *
 * MatchMedia is shared process-wide — only three `change` listeners are
 * registered. Pass `enabled: false` when the node has no `showOn` so the
 * caller does not subscribe / re-render on resize (Rules of Hooks: still
 * call the hook; skip the subscription).
 */
export function useActiveVisibilityBreakpoints(
  enabled: boolean = true,
): ReadonlySet<string> {
  const [active, setActive] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => {
    if (!enabled) {
      return;
    }

    startSharedSubscription();

    const listener: ActiveListener = (next) => {
      setActive(next);
    };
    subscribers.add(listener);
    // Sync immediately if the shared subscription already has a value.
    setActive(currentActive);

    return () => {
      subscribers.delete(listener);
    };
  }, [enabled]);

  return active;
}
